import {
	type AssetType,
	EMPTY_ARRAY,
	type ProcedureInitiative,
	submittedAtModelSortFn,
	toUuidArray,
} from "@overmap-ai/common"
import {
	restructureSelectorWithArgs,
	selectIssueTypeStatuses,
	selectProcedureInitiativesMapping,
} from "@overmap-ai/core"
import { createSelector } from "@reduxjs/toolkit"

import { DEFAULT_HIDDEN_ISSUE_STATUS_TYPES } from "../../constants"
import { IssueTypeStatusFilterDef } from "../../filtering/defs/IssueStatusFilterDef"
import type { AnySerializedFilter } from "../../filtering/filter"
import type { RootSelector } from "../typings"

export const selectAssetTypeMapFiltersMapping: RootSelector<Record<string, AnySerializedFilter[]>> = (state) => {
	return state.mapFilters.assetTypeFilters
}

export const selectAssetTypeMapFiltersById = restructureSelectorWithArgs(
	createSelector([selectAssetTypeMapFiltersMapping, (_, id: string) => id], (assetTypeFilters, id) => {
		return assetTypeFilters[id] ?? EMPTY_ARRAY
	}),
)

export const selectIssueTypeMapFiltersMapping: RootSelector<Record<string, AnySerializedFilter[]>> = (state) => {
	return state.mapFilters.issueTypeFilters
}

export const selectIssueTypeMapFiltersById = restructureSelectorWithArgs(
	createSelector(
		[selectIssueTypeMapFiltersMapping, selectIssueTypeStatuses, (_, id: string) => id],
		(issueTypeFilters, issueTypeStatuses, id) => {
			const hiddenIssueStatusSet = new Set(DEFAULT_HIDDEN_ISSUE_STATUS_TYPES)
			const issueTypeStatusesToFilter = issueTypeStatuses.filter(
				({ type, issue_type }) => issue_type === id && hiddenIssueStatusSet.has(type),
			)

			const filterDef = new IssueTypeStatusFilterDef(issueTypeStatusesToFilter)

			const filter = filterDef.createFilter({
				filterModifier: "excludes",
				filterValue: toUuidArray(issueTypeStatusesToFilter),
			})

			return issueTypeFilters[id] ?? [filterDef.serialize(filter)]
		},
	),
)

export const selectProcedureInitiativeMapFiltersMapping: RootSelector<
	Record<ProcedureInitiative["uuid"], AnySerializedFilter[]>
> = (state) => {
	return state.mapFilters.procedureInitiativeFilters
}

export const selectProcedureInitiativeMapFiltersById = restructureSelectorWithArgs(
	createSelector(
		[selectProcedureInitiativeMapFiltersMapping, (_, id: ProcedureInitiative["uuid"]) => id],
		(procedureInitiativeFiltersMapping, id) => {
			return procedureInitiativeFiltersMapping[id] ?? EMPTY_ARRAY
		},
	),
)

export const selectActiveProcedureInitiativeMapping: RootSelector<
	Record<AssetType["uuid"], ProcedureInitiative["uuid"] | null>
> = (state) => {
	return state.mapFilters.activeProcedureInitiatives
}

export const selectActiveProcedureInitiativeOfAssetType = restructureSelectorWithArgs(
	createSelector(
		[selectProcedureInitiativesMapping, selectActiveProcedureInitiativeMapping, (_, id: AssetType["uuid"]) => id],
		(procedureInitiativeMapping, activeProcedureInitiativeMapping, id) => {
			if (!(id in activeProcedureInitiativeMapping)) {
				// TODO: Is this really a good idea?
				// TOOD: replace with submittedAtSortFn when exported from /core
				const latestProcedureInitiative = Object.values(procedureInitiativeMapping)
					.filter(({ asset_type }) => asset_type === id)
					.toSorted(submittedAtModelSortFn)
					.pop()

				return latestProcedureInitiative ?? null
			}

			const procedureInitiativeId = activeProcedureInitiativeMapping[id] ?? null

			if (!procedureInitiativeId) return null

			return procedureInitiativeMapping[procedureInitiativeId] ?? null
		},
	),
)
